import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { PLANS } from '../lib/config'
import { submitContactMessage, getPlanPricing } from '../lib/supabase'

export default function Landing() {
  const navigate = useNavigate()
  const [contact, setContact] = useState({ name: '', email: '', message: '' })
  const [contactSent, setContactSent] = useState(false)
  const [contactLoading, setContactLoading] = useState(false)
  const [livePrices, setLivePrices] = useState({})

  useEffect(() => {
    getPlanPricing().then(({ data }) => {
      if (data) setLivePrices(Object.fromEntries(data.map(p => [p.plan_key, p.price])))
    })
  }, [])

  function getPrice(key, fallback) { return livePrices[key] ?? fallback }

  async function handleContact(e) {
    e.preventDefault()
    setContactLoading(true)
    await submitContactMessage(contact)
    setContactSent(true)
    setContactLoading(false)
  }

  return (
    <div style={{ fontFamily: 'system-ui,-apple-system,sans-serif', color: '#111827', background: '#fff', overflowX: 'hidden' }}>
      <style>{`
        *{box-sizing:border-box;margin:0;padding:0}
        .lp{max-width:1080px;margin:0 auto;padding:0 clamp(1rem,4vw,2rem)}
        .lp-btn{display:inline-flex;align-items:center;gap:6px;padding:11px 24px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;border:none;transition:all 0.15s;text-decoration:none;font-family:inherit}
        .lp-btn:hover{opacity:0.88;transform:translateY(-1px)}
        .lp-primary{background:#2563eb;color:#fff}
        .lp-outline{background:transparent;color:#2563eb;border:1.5px solid #2563eb}
        .lp-dark{background:#1e293b;color:#fff}
        .lp-ghost{background:transparent;color:#6b7280;border:1.5px solid #e5e7eb}
        .lp-ghost:hover{background:#f9fafb;color:#111827}
        .lp-section{padding:clamp(3.5rem,7vw,5.5rem) 0}
        .lp-label{font-size:12px;font-weight:700;letter-spacing:0.08em;text-transform:uppercase;color:#2563eb;margin-bottom:0.6rem}
        .lp-h1{font-size:clamp(2.2rem,5.5vw,3.5rem);font-weight:900;letter-spacing:-1.5px;line-height:1.1;color:#0f172a}
        .lp-h2{font-size:clamp(1.6rem,3.5vw,2.2rem);font-weight:800;letter-spacing:-0.5px;line-height:1.2}
        .lp-sub{font-size:16px;color:#64748b;line-height:1.75}
        .lp-card{background:#f8fafc;border:1px solid #e2e8f0;border-radius:12px;padding:1.5rem}
        lp-field{display:block;width:100%;padding:10px 12px;border:1.5px solid #d1d5db;border-radius:8px;font-size:14px;font-family:inherit;outline:none;transition:border-color 0.15s}
        input:focus,textarea:focus{border-color:#2563eb}
        .check{color:#16a34a;font-weight:700;margin-right:6px}
        .cross{color:#9ca3af;font-weight:700;margin-right:6px}
      `}</style>

      {/* Nav */}
      <nav style={{ position: 'sticky', top: 0, background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)', borderBottom: '1px solid #e5e7eb', zIndex: 50 }}>
        <div className="lp" style={{ height: 58, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontWeight: 800, fontSize: 18, letterSpacing: -0.5 }}>Campus<span style={{ color: '#2563eb' }}>Flow</span></div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="lp-btn lp-ghost" style={{ padding: '7px 14px', fontSize: 13 }} onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}>Pricing</button>
            <button className="lp-btn lp-ghost" style={{ padding: '7px 14px', fontSize: 13 }} onClick={() => navigate('/login')}>Sign in</button>
            <button className="lp-btn lp-primary" style={{ padding: '8px 18px', fontSize: 13 }} onClick={() => navigate('/signup')}>Try free →</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <div style={{ background: 'linear-gradient(180deg,#eff6ff 0%,#fff 100%)', borderBottom: '1px solid #dbeafe' }}>
        <div className="lp lp-section" style={{ textAlign: 'center' }}>
          <div className="lp-label">Attendance management · Built for Ghana</div>
          <h1 className="lp-h1" style={{ marginBottom: '1.25rem', maxWidth: 720, margin: '0 auto 1.25rem' }}>
            Know exactly who showed up.<br />GPS-verified. Fraud-proof.
          </h1>
          <p className="lp-sub" style={{ maxWidth: 520, margin: '0 auto 2rem' }}>
            Members check in from their phone — no app needed. GPS lock, device fingerprint, and time-gating prevent every kind of proxy attendance.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            <button className="lp-btn lp-primary" style={{ fontSize: 15, padding: '13px 30px' }} onClick={() => navigate('/signup')}>Start 14-day free trial →</button>
            <button className="lp-btn lp-outline" style={{ fontSize: 15, padding: '13px 30px' }} onClick={() => navigate('/attend')}>Mark attendance</button>
          </div>
          <p style={{ fontSize: 13, color: '#94a3b8' }}>No payment required to start · From GH₵{getPrice('starter', 150)}/year</p>
        </div>
      </div>

      {/* How it works */}
      <div className="lp-section" id="how">
        <div className="lp">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div className="lp-label">Simple process</div>
            <h2 className="lp-h2">Running in under 10 minutes</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '2rem' }}>
            {[
              { n: '1', icon: '🏢', title: 'Register your org', desc: 'Sign up, choose your org type, customise labels. No payment needed — 14-day full trial starts immediately.' },
              { n: '2', icon: '📤', title: 'Upload your members', desc: 'Import from Excel or CSV (Pro) or add manually (Starter). Set sessions and GPS zones for each venue.' },
              { n: '3', icon: '📲', title: 'Share your code', desc: 'Send the 8-character code to your members. They open the attend page in any browser — no download.' },
              { n: '4', icon: '✅', title: 'Track it live', desc: 'GPS verified. Device locked. Time gated. Dashboard updates in real time as members check in.' },
            ].map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: '1rem' }}>
                <div style={{ width: 34, height: 34, borderRadius: 8, background: '#dbeafe', color: '#2563eb', fontWeight: 800, fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{s.n}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 5 }}>{s.icon} {s.title}</div>
                  <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.7 }}>{s.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <hr style={{ border: 'none', borderTop: '1px solid #e5e7eb' }} />

      {/* Features */}
      <div className="lp-section">
        <div className="lp">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div className="lp-label">Built right</div>
            <h2 className="lp-h2">Every angle covered</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '1rem' }}>
            {[
              { icon: '📍', title: 'GPS zone locking', desc: 'Set a radius around your venue. Members must be physically inside to check in. You calibrate it live from your phone.' },
              { icon: '📱', title: 'Device fingerprinting', desc: 'One check-in per device per session. No sharing, no workarounds. Blocked attempts are logged.' },
              { icon: '⏱️', title: 'Time-gated sessions', desc: 'Attendance only opens during the scheduled window — not a minute before or after.' },
              { icon: '👨‍🏫', title: 'Lecturer access', desc: 'Invite lecturers with a shareable link. They see only their sessions — no editing access.' },
              { icon: '📢', title: 'Announcement board', desc: 'Post a notice that every member sees when they open the attend portal.' },
              { icon: '📊', title: 'Analytics & alerts', desc: 'Attendance charts, member scorecards, and at-risk alerts for members below 50% (Pro).' },
            ].map((f, i) => (
              <div key={i} className="lp-card" style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <div style={{ fontSize: 22, flexShrink: 0 }}>{f.icon}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{f.title}</div>
                  <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.65 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="lp-section" id="pricing" style={{ background: '#f8fafc' }}>
        <div className="lp">
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <div className="lp-label">Transparent pricing</div>
            <h2 className="lp-h2">One price per year. No surprises.</h2>
            <p className="lp-sub" style={{ marginTop: '0.5rem' }}>Start free for 14 days. Pay only when your trial ends.</p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(300px,1fr))', gap: '1.5rem', maxWidth: 720, margin: '0 auto' }}>
            {PLANS.map(plan => {
              const price = getPrice(plan.key, plan.price)
              return (
                <div key={plan.key} style={{
                  border: `2px solid ${plan.popular ? '#2563eb' : '#e2e8f0'}`,
                  borderRadius: 16, padding: '2rem', background: plan.popular ? '#eff6ff' : '#fff',
                  position: 'relative',
                }}>
                  {plan.popular && (
                    <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: '#2563eb', color: '#fff', borderRadius: 99, padding: '3px 16px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      RECOMMENDED
                    </div>
                  )}
                  <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 4 }}>{plan.name}</div>
                  <div style={{ marginBottom: '1.5rem' }}>
                    <span style={{ fontSize: 36, fontWeight: 900, letterSpacing: -1.5 }}>GH₵{price}</span>
                    <span style={{ fontSize: 13, color: '#64748b', marginLeft: 6 }}>/ year</span>
                  </div>
                  <button className="lp-btn lp-primary" style={{ width: '100%', justifyContent: 'center', marginBottom: '1.5rem', background: plan.popular ? '#2563eb' : '#1e293b' }} onClick={() => navigate('/signup', { state: { plan: plan.key } })}>
                    Start free trial →
                  </button>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                    {plan.features.map((f, i) => <div key={i} style={{ fontSize: 13, color: '#374151' }}><span className="check">✓</span>{f}</div>)}
                    {plan.missing?.map((f, i) => <div key={i} style={{ fontSize: 13, color: '#94a3b8' }}><span className="cross">✗</span>{f}</div>)}
                  </div>
                </div>
              )
            })}
          </div>
          <p style={{ textAlign: 'center', fontSize: 13, color: '#94a3b8', marginTop: '1.5rem' }}>
            Both plans include a 14-day free trial with full access. No credit card required.
          </p>
        </div>
      </div>

      {/* CTA */}
      <div style={{ background: '#1e293b', padding: 'clamp(3rem,6vw,5rem) 0', textAlign: 'center' }}>
        <div className="lp">
          <h2 style={{ fontSize: 'clamp(1.6rem,3.5vw,2.2rem)', fontWeight: 800, color: '#fff', marginBottom: '1rem' }}>
            Ready to stop guessing who attended?
          </h2>
          <p style={{ color: '#94a3b8', fontSize: 15, marginBottom: '2rem' }}>Set up in under 10 minutes. No technical skills needed.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button className="lp-btn" style={{ background: '#fff', color: '#1e293b', fontSize: 15, padding: '13px 30px', fontWeight: 700 }} onClick={() => navigate('/signup')}>
              Start your free trial →
            </button>
            <button className="lp-btn" style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: 15, padding: '13px 30px', border: '1.5px solid rgba(255,255,255,0.2)' }} onClick={() => navigate('/attend')}>
              Try the attend portal
            </button>
          </div>
        </div>
      </div>

      {/* Contact */}
      <div className="lp-section" id="contact">
        <div className="lp" style={{ maxWidth: 520, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
            <div className="lp-label">Contact</div>
            <h2 className="lp-h2">Questions? We reply fast.</h2>
          </div>
          {contactSent ? (
            <div className="lp-card" style={{ textAlign: 'center', padding: '2rem' }}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
              <div style={{ fontWeight: 700 }}>Message received! We'll get back to you shortly.</div>
            </div>
          ) : (
            <form onSubmit={handleContact} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[['Name', 'name', 'text'], ['Email', 'email', 'email']].map(([label, key, type]) => (
                <div key={key}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 5, color: '#374151' }}>{label}</label>
                  <input type={type} required placeholder={label} value={contact[key]} onChange={e => setContact({ ...contact, [key]: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit' }} />
                </div>
              ))}
              <div>
                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 5, color: '#374151' }}>Message</label>
                <textarea required placeholder="Your message..." value={contact.message} onChange={e => setContact({ ...contact, message: e.target.value })}
                  style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #d1d5db', borderRadius: 8, fontSize: 14, outline: 'none', fontFamily: 'inherit', minHeight: 100, resize: 'vertical' }} />
              </div>
              <button type="submit" className="lp-btn lp-primary" disabled={contactLoading} style={{ justifyContent: 'center' }}>
                {contactLoading ? 'Sending...' : 'Send message →'}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid #e5e7eb', padding: '1.5rem 0', background: '#f9fafb' }}>
        <div className="lp" style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '1rem' }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>Campus<span style={{ color: '#2563eb' }}>Flow</span></div>
          <div style={{ display: 'flex', gap: '1.5rem' }}>
            {[['Attend', '/attend'], ['Login', '/login'], ['Register', '/signup']].map(([label, path]) => (
              <span key={path} style={{ fontSize: 13, color: '#64748b', cursor: 'pointer' }} onClick={() => navigate(path)}>{label}</span>
            ))}
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>© 2026 CampusFlow · Built in Ghana 🇬🇭</div>
        </div>
      </div>
    </div>
  )
}
